import { ModelType } from '@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { ExtractionType } from '@/modules/ai/constants/ExtractionType';
import { ExtractedEntityType } from '@/modules/ai/constants/ExtractedEntityType';
import { ExtractedUserResidualBeneficiaryMeta } from '@/modules/ai/models/meta/ExtractedUserResidualBeneficiaryMeta';
import { ExtractedUserSpecificBeneficiaryMeta } from '@/modules/ai/models/meta/ExtractedUserSpecificBeneficiaryMeta';
import { ExtractedUserMetaFactory } from '@/modules/ai/models/meta/ExtractedUserMetaFactory';
import { ExtractedUserMetaType } from '@/modules/ai/constants/ExtractedUserMetaType';
import { ExtractedEntityCategoryTypeKey } from '@/modules/ai/constants/ExtractedEntityCategoryTypeKey';
import { EstateRelationshipType } from '/@/modules/estate/enums/EstateRelationshipType';
import { DocumentExtraction } from '@/modules/ai/models/DocumentExtraction';
import { ExtractedContact } from '@/modules/ai/models/ExtractedContact';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';

export class ExtractedUser extends BaseEntityModel {
  id?: number;
  documentExtractionId?: number;
  extractedContactId?: number;
  estateAccountId?: number;
  metaType?: ExtractedUserMetaType;
  meta?: ExtractedUserResidualBeneficiaryMeta | ExtractedUserSpecificBeneficiaryMeta | undefined;
  extractionType?: ExtractionType;
  category?: ExtractedEntityCategoryTypeKey;
  type?: ExtractedEntityType;
  relationToTestator?: string;
  relationSpecify?: string;
  relationToTestatorOther?: string;
  note?: string;
  isValidated?: boolean;
  documentExtraction?: Relationship<DocumentExtraction | null>;
  extractedContact?: Relationship<ExtractedContact | null>;
  estateAccount?: Relationship<EstateAccount | null>;

  constructor(model: Partial<ExtractedUser> = {}) {
    super(model);

    this.id = model.id;
    this.documentExtractionId = model.documentExtractionId;
    this.extractedContactId = model.extractedContactId;
    this.estateAccountId = model.estateAccountId;
    this.metaType = model.metaType;
    this.meta = model.meta ? ExtractedUserMetaFactory.build(model.metaType, model.meta) : undefined;
    this.extractionType = model.extractionType;
    this.category = model.category;
    this.type = model.type;
    this.relationToTestator = model.relationToTestator;
    this.relationSpecify = model.relationSpecify;
    this.relationToTestatorOther = model.relationToTestatorOther;
    this.note = model.note;
    this.isValidated = model.isValidated;
    this.documentExtraction = DocumentExtraction.mapModelOrNull(model.documentExtraction);
    this.extractedContact = ExtractedContact.mapModelOrNull(model.extractedContact);
    this.estateAccount = EstateAccount.mapModelOrNull(model.estateAccount);
  }

  getModelName(): ModelType {
    return ModelType.EXTRACTED_USER;
  }

  get displayRelationToTestator(): string {
    return EstateRelationshipType.getByKey(this.relationToTestator)?.value ?? '';
  }

  get name(): string {
    return this.extractedContact?.name ?? '';
  }

  get avatarLetters(): string {
    return this.extractedContact?.hasName ? this.extractedContact.firstLetters : '-';
  }

  get isOtherRelationToTestator(): boolean {
    return this.relationToTestator === EstateRelationshipType.OTHER.key;
  }

  get isEditableUser(): boolean {
    return !!this.extractedContact?.hasName && !this.isValidated;
  }

  get displayRelationToTestatorFormatted(): string {
    return this.relationToTestator ? this.displayRelationToTestator : '-';
  }

  get hasRequiredInputs(): boolean {
    return (
      !!this.extractedContact?.firstName &&
      !!this.extractedContact.lastName &&
      !!this.relationToTestator
    );
  }

  setExtractedUserRelationshipFromEstateAccount(): void {
    this.relationToTestator = this.estateAccount?.meta.relationshipType;

    if (this.relationToTestator === EstateRelationshipType.OTHER.key) {
      this.relationSpecify = this.estateAccount?.meta.otherRelationshipType;
    }
  }

  setExtractedContactFromContact(): void {
    // @ts-ignore
    this.extractedContact.firstName = this.estateAccount?.user?.contact?.firstName;
    // @ts-ignore
    this.extractedContact.middleName = this.estateAccount?.user?.contact?.middleName;
    // @ts-ignore
    this.extractedContact.lastName = this.estateAccount?.user?.contact?.lastName;
    // @ts-ignore
    this.extractedContact.personTitle = this.estateAccount?.user?.contact?.personTitle;
    // @ts-ignore
    this.extractedContact.birthSurname = this.estateAccount?.user?.contact?.birthSurname;
  }
}
